import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";

const initialState = {
  list: [],
  loading: false,
  error: null,
  privileges: [],
  privilegesLoading: false,
  refreshNeeded: false,
  userRoles: [],
  userRolesLoading: false,
  activeRole: null,
  activePrivileges: [],
  switchLoading: false,
  switchError: null,
};

export const fetchRoles = createAsyncThunk(
  "role/fetchRoles",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", `RoleConfig/Role_GetRoleList?CompanyId=${companyId}`);
      return res.data;
    } catch (err) {
      console.error("Fetch roles error:", err);
      return rejectWithValue(err);
    }
  }
);

export const fetchRolePrivileges = createAsyncThunk(
  "role/fetchRolePrivileges",
  async ({ roleId, companyId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", `RoleConfig/Role_GetPrivilege?RoleID=${roleId}&CompanyId=${companyId}`);
      return res.data;
    } catch (err) {
      console.error("Failed to fetch privileges:", err);
      return rejectWithValue(err);
    }
  }
);

export const createUpdateRole = createAsyncThunk(
  "role/createUpdateRole",
  async ({ roleData, companyId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest("POST", `RoleConfig/Role_CreateUpdateRole?CompanyId=${companyId}`, roleData);
      return { data: res.data, roleData };
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err);
    }
  }
);

export const deleteRole = createAsyncThunk(
  "role/deleteRole",
  async ({ rID, user, crDate }, { rejectWithValue }) => {
    try {
      const res = await apiRequest("POST", `RoleConfig/Role_DeleteRole?rID=${rID}&user=${user}&crDate=${crDate}`);
      return { rID, message: res.data };
    } catch (err) {
      console.error("Failed to delete role:", err);
      return rejectWithValue(err);
    }
  }
);

export const fetchUserRoles = createAsyncThunk(
  "role/fetchUserRoles",
  async (employeeId, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", `RoleConfig/GetUserRoles/${employeeId}`);
      return res.data?.Data ?? res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const switchUserRole = createAsyncThunk(
  "role/switchUserRole",
  async ({ employeeId, roleId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `RoleConfig/GetUserRolesAndPrivileges?employeeId=${employeeId}&roleId=${roleId}`
      );
      return res.data?.Data ?? res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const role_UpdateSortOrder = createAsyncThunk(
  "role/Role_UpdateSortOrder",
  async (data) => await apiRequest("POST", "RoleConfig/Role_UpdateSortOrder", data, true)
);

// ─── Normalise privileges from any API shape → "EMPVIEW,EMPCREATE,..." ────────
function normalisePrivileges(privileges) {
  if (!privileges) return null;

  // Plain comma-separated string: "EMPVIEW,EMPCREATE,..."
  if (typeof privileges === "string") {
    if (privileges.trim().startsWith("[") || privileges.trim().startsWith("{")) {
      try {
        return normalisePrivileges(JSON.parse(privileges)); // recurse
      } catch {
        return privileges.trim();
      }
    }
    return privileges.trim() || null;
  }

  // Array of strings: ["EMPVIEW", "EMPCREATE", ...]
  if (Array.isArray(privileges) && privileges.length > 0) {
    if (typeof privileges[0] === "string") {
      return privileges.join(",");
    }
    // Array of objects: [{ PrivilegeCode: "EMPVIEW" }, ...]
    if (typeof privileges[0] === "object") {
      const codes = privileges
        .map((p) => p?.PrivilegeCode ?? p?.Code ?? "")
        .filter(Boolean);
      return codes.length > 0 ? codes.join(",") : null;
    }
  }

  return null; // empty array or unrecognised — don't overwrite
}
// ──────────────────────────────────────────────────────────────────────────────

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    clearError:     (state) => { state.error = null; },
    clearRoles:     (state) => { state.list = []; },
    clearPrivileges:(state) => { state.privileges = []; },
  },
  extraReducers: (builder) => {
    builder
      // fetchRoles
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load roles";
      })

      // fetchRolePrivileges
      .addCase(fetchRolePrivileges.pending, (state) => {
        state.privilegesLoading = true;
        state.error = null;
      })
      .addCase(fetchRolePrivileges.fulfilled, (state, action) => {
        state.privilegesLoading = false;
        state.privileges = action.payload || [];
      })
      .addCase(fetchRolePrivileges.rejected, (state, action) => {
        state.privilegesLoading = false;
        state.error = action.payload?.message || "Failed to load privileges";
      })

      // createUpdateRole
      .addCase(createUpdateRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUpdateRole.fulfilled, (state) => {
        state.loading = false;
        state.refreshNeeded = true;
      })
      .addCase(createUpdateRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to save role";
      })

      // deleteRole
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(
          (role) => role.RoleID !== parseInt(action.payload.rID)
        );
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete role";
      })

      // fetchUserRoles
      .addCase(fetchUserRoles.pending, (state) => {
        state.userRolesLoading = true;
        state.switchError = null;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.userRolesLoading = false;
        state.userRoles = action.payload || [];
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        state.userRolesLoading = false;
        state.switchError = action.payload?.message || "Failed to load user roles";
      })

      // switchUserRole
      .addCase(switchUserRole.pending, (state) => {
        state.switchLoading = true;
        state.switchError = null;
      })
      .addCase(switchUserRole.fulfilled, (state, action) => {
        state.switchLoading = false;
        const payload = action.payload;

        const roleId     = payload?.role?.RoleID   ?? payload?.RoleID;
        const roleName   = payload?.role?.RoleName ?? payload?.RoleName;
        const privileges = payload?.privileges     ?? payload?.UserPrivileges ?? null;

        state.activeRole       = payload?.role ?? payload;
        state.activePrivileges = Array.isArray(privileges) ? privileges : [];

        if (roleId)   localStorage.setItem("roleId",   roleId);
        if (roleName) localStorage.setItem("roleName", roleName);

        // ✅ Only overwrite Privillages when API actually returned privilege data
        const normalised = normalisePrivileges(privileges);
        if (normalised) {
          localStorage.setItem("Privillages", normalised);
          window.dispatchEvent(new CustomEvent("privilegesChanged"));
        }
        // If null → login-time Privillages remain untouched
      })
      .addCase(switchUserRole.rejected, (state, action) => {
        state.switchLoading = false;
        state.switchError = action.payload?.message || "Failed to switch role";
      });
  },
});

export const { clearError, clearRoles, clearPrivileges } = roleSlice.actions;
export default roleSlice.reducer;